import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  Row,
  Select,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import {
  formatString,
  navigateTo,
  getUserBranch,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams, useLocation } from "react-router-dom";

const { Option } = Select;

interface PettyCashEntryFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  review?: boolean;
}

const PettyCashEntryForm: React.FC<PettyCashEntryFormProps> = ({
  add: addProp,
  edit: editProp,
  view: viewProp,
  review: reviewProp,
}) => {
  const [loading, setLoading]               = useState("");
  const [form]                              = Form.useForm();
  const [branches, setBranches]: any        = useState([]);
  const [departments, setDepartment]: any    = useState([]);
  const [status, setStatus]                 = useState("");
  const { id: petty_cash_entry_id }         = useParams<{ id: string }>();
  const location                            = useLocation();
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [disapproveRemarks,   setDisapproveRemarks]   = useState("");
  const [isBranchLocked, setIsBranchLocked] = useState(false);

  const path = location.pathname;
  const add = addProp || path.endsWith("/add");
  const edit = editProp || path.includes("/edit/");
  const review = reviewProp || path.includes("/review/");
  const view = viewProp || (path.includes("/view/") && !review);
  const isReadOnly = !!(view || review);
  const canSubmit = !!(add || edit);

  function clearFields() {
    form.resetFields();
  }

  async function submitForm(data: any) {
    const selectedDept = departments.find(
      (d: any) => String(d.id) === String(data.department_id)
    );
    const formattedData: any = {
      account:          data.account,
      department_id:    data.department_id,
      department:       selectedDept?.name ?? data.department ?? "",
      branch_id:        data.branch_id,
      transaction_date: data.transaction_date
        ? dayjs(data.transaction_date).format("YYYY-MM-DD")
        : "",
      requested_by: data.requested_by ?? null,
      amount:       data.amount,
      remarks:      data.remarks ?? null,
    };

    const endpoint = edit
      ? `petty_cash_entries/update`
      : `petty_cash_entries/create`;

    if (edit) {
      formattedData.petty_cash_entry_id = petty_cash_entry_id;
    }

    setLoading("submit");
    const response = await postRequest(endpoint, formattedData, true);

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-entry");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${edit ? "update" : "create"} petty cash entry.`
      );
    }
  }

  async function submitApprove() {
    setLoading("approve");
    const response = await postRequest(
      `petty_cash_entries/change_status`,
      { petty_cash_entry_id, action: "approved" },
      true
    );

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-entry");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve petty cash entry."
      );
    }
  }

  async function submitDisapprove() {
    if (!disapproveRemarks.trim()) {
      message.error("Please provide a reason for disapproval.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("disapprove");
    const response = await postRequest(
      `petty_cash_entries/change_status`,
      { petty_cash_entry_id, action: "disapproved", reason: disapproveRemarks },
      true
    );

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-entry");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove petty cash entry."
      );
    }
  }

  async function fetchBranches() {
    setBranches([]);
    const response = await getRequest(`branches/get`, {}, true);
    if (response.success) {
      setBranches(response.data.data);
    }
  }

  async function fetchDepartment() {
    setDepartment([]);
    const response = await getRequest(`departments/get`, {}, true);
    if (response.success) {
      setDepartment(response.data.data ?? []);
      return response.data.data ?? [];
    }
    return [];
  }

  async function fetchEntry(departmentList: any[] = []) {
    if (!petty_cash_entry_id) return;

    setLoading("fetch");
    const response = await getRequest(
      `petty_cash_entries/get`,
      { petty_cash_entry_id },
      true
    );

    if (response.success) {
      const entry = response.data.data[0];
      const matched = (departmentList.length ? departmentList : departments).find(
        (d: any) => String(d.id) === String(entry.department_id)
      );
      setStatus(entry.status);
      form.setFieldsValue({
        account:          entry.account,
        department_id:    entry.department_id || matched?.id || undefined,
        branch_id:        entry.branch_id,
        transaction_date: entry.transaction_date
          ? dayjs(entry.transaction_date)
          : null,
        requested_by: entry.requested_by,
        amount:       entry.amount,
        remarks:      entry.remarks,
      });
    }
    setLoading("");
  }

  useEffect(() => {
    (async () => {
      setLoading("fetch");
      await fetchBranches();
      const departmentList = await fetchDepartment();
      if (edit || view || review) {
        await fetchEntry(departmentList);
      } else {
        setLoading("");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!add) return;
    const branchId = getUserBranch();
    if (branchId && branches.length > 0) {
      form.setFieldsValue({ branch_id: branchId });
      setIsBranchLocked(true);
    }
  }, [branches]);

  const pageTitle = add
    ? "Add Petty Cash Entry"
    : edit
    ? "Edit Petty Cash Entry"
    : "View Petty Cash Entry";

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={pageTitle}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>
          <div className="form-container w-full">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Account or Check Number"
                      name="account"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Department"
                      name="department_id"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select department"
                        showSearch
                        allowClear
                        disabled={isReadOnly}
                        optionFilterProp="label"
                        options={departments.map((department: any) => ({
                          value: department.id,
                          label: department.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Branch"
                      name="branch_id"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select branch"
                        showSearch
                        allowClear
                        disabled={isReadOnly || isBranchLocked}
                        loading={loading === "fetch"}
                        filterOption={(input: string, option?: any) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {branches.map((branch: any) => (
                          <Option key={branch.id} value={branch.id}>
                            {branch.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Transaction Date"
                      name="transaction_date"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <DatePicker className="w-full" format={"MMMM D, YYYY"} disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Requested By"
                      name="requested_by"
                      rules={[{ required: !isReadOnly, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Amount"
                      name="amount"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isReadOnly}
                        onKeyDown={(event) => {
                          const isDot   = event.key === ".";
                          const hasDot  = event.currentTarget.value.includes(".");
                          const allowed = /[0-9.]/;
                          if (!allowed.test(event.key) || (isDot && hasDot)) {
                            event.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  {/* Footer Buttons */}
                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Form.Item>
                      <Button
                        type="primary"
                        onClick={() => {
                          navigateTo("/petty-cash-entry");
                          clearFields();
                        }}
                      >
                        Close
                      </Button>
                    </Form.Item>

                    {/* Approve / Disapprove — only on view mode when status is pending */}
                    {review && status === "pending" && (
                      <>
                        <Form.Item>
                          <Button
                            danger
                            loading={loading === "disapprove"}
                            onClick={() => { setDisapproveRemarks(""); setShowDisapproveModal(true); }}
                          >
                            Disapprove
                          </Button>
                        </Form.Item>
                        <Form.Item>
                          <ConfirmAction
                            title="Are you sure you want to approve this petty cash entry?"
                            onConfirm={submitApprove}
                            okText="Approve"
                            cancelText="Cancel"
                            okButtonProps={{ loading: loading === "approve" }}
                          >
                            <Button type="primary" loading={loading === "approve"}>
                              Approve
                            </Button>
                          </ConfirmAction>
                        </Form.Item>
                      </>
                    )}

                    {/* Submit / Update — only on add or edit mode */}
                    {canSubmit && (
                      <Form.Item>
                        <Button
                          htmlType="submit"
                          type="primary"
                          loading={loading === "submit"}
                        >
                          {edit ? "Update" : "Submit"}
                        </Button>
                      </Form.Item>
                    )}
                  </Col>

                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>
      <PersistentFooter />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={disapproveRemarks}
        onReasonChange={(e: any) => setDisapproveRemarks(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "disapprove"}
            disabled={!disapproveRemarks.trim()}
            onClick={submitDisapprove}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />
    </Layout>
  );
};

export default PettyCashEntryForm;